import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Card,
  CardBody,
  HStack,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import Papa from 'papaparse';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

type ParsedNrtEntry = {
  code: string;
  title: string;
  type?: string;
  status?: string;
};

const CHUNK_SIZE = 2000;

export function normaliseHeaders(headers: string[]): Map<string, string> {
  const mapping = new Map<string, string>();
  for (const header of headers) {
    const lower = header.toLowerCase().trim();
    if (
      lower === 'code' ||
      lower === 'unit code' ||
      lower === 'unitcode' ||
      lower === 'national code'
    ) {
      mapping.set(header, 'code');
    } else if (
      lower === 'title' ||
      lower === 'unit title' ||
      lower === 'unittitle' ||
      lower === 'name' ||
      lower === 'national title'
    ) {
      mapping.set(header, 'title');
    } else if (lower === 'type' || lower === 'nrt type') {
      mapping.set(header, 'type');
    } else if (lower === 'status' || lower === 'nrt status') {
      mapping.set(header, 'status');
    }
  }
  return mapping;
}

export function NrtRegisterUpload() {
  const trpcUtils = trpcAdmin.useUtils();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsedEntries, setParsedEntries] = useState<ParsedNrtEntry[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const uploadMutation = trpcAdmin.unitsOfCompetency.uploadNrtRegister.useMutation();

  const handleUpload = async () => {
    setIsUploading(true);
    setUploadError(null);
    setUploadResult(null);
    let totalCreated = 0;
    let totalUpdated = 0;

    try {
      for (let i = 0; i < parsedEntries.length; i += CHUNK_SIZE) {
        const chunk = parsedEntries.slice(i, i + CHUNK_SIZE);
        const result = await uploadMutation.mutateAsync({ units: chunk });
        totalCreated += result.created;
        totalUpdated += result.updated;
      }

      void trpcUtils.unitsOfCompetency.searchNrtRegister.invalidate();
      setParsedEntries([]);
      setFileName(null);
      setUploadResult(
        t`Imported ${totalCreated + totalUpdated} entries (${totalCreated} new, ${totalUpdated} updated)`,
      );
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : t`Failed to import entries`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file == null) {
      return;
    }

    setParseError(null);
    setUploadError(null);
    setUploadResult(null);
    setParsedEntries([]);
    setFileName(file.name);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.meta.fields == null || results.meta.fields.length === 0) {
          setParseError(t`Could not detect CSV headers.`);
          return;
        }

        const headerMap = normaliseHeaders(results.meta.fields);
        const codeField = [...headerMap.entries()].find(([, v]) => v === 'code')?.[0];
        const titleField = [...headerMap.entries()].find(([, v]) => v === 'title')?.[0];

        if (codeField == null || titleField == null) {
          setParseError(
            t`CSV must have "Code" and "Title" columns (found: ${results.meta.fields.join(', ')}).`,
          );
          return;
        }

        const typeField = [...headerMap.entries()].find(([, v]) => v === 'type')?.[0];
        const statusField = [...headerMap.entries()].find(([, v]) => v === 'status')?.[0];

        const units: ParsedNrtEntry[] = [];
        for (const row of results.data) {
          const code = row[codeField]?.trim();
          const title = row[titleField]?.trim();
          if (code && title) {
            units.push({
              code,
              title,
              type: typeField ? row[typeField]?.trim() : undefined,
              status: statusField ? row[statusField]?.trim() : undefined,
            });
          }
        }

        if (units.length === 0) {
          setParseError(t`No valid rows found in CSV.`);
          return;
        }

        setParsedEntries(units);
      },
      error: (error) => {
        setParseError(error.message);
      },
    });

    event.target.value = '';
  };

  return (
    <Card variant="outline" mt={3} size="sm">
      <CardBody p={3}>
        <VStack spacing={3} align="stretch">
          <Text fontSize="xs" color="fg.subtle">
            <Trans>
              Export from the{' '}
              <Link
                href="https://training.gov.au/search?searchText=&searchType=NRT&status=0"
                isExternal
                color="blue.500"
              >
                training.gov.au NRT search
              </Link>{' '}
              and upload the CSV here. Needs "Code" and "Title" columns. Existing entries will be
              updated, new entries will be added.
            </Trans>
          </Text>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          <HStack spacing={2}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<Upload size={14} />}
              onClick={() => fileInputRef.current?.click()}
              minW={0}
              flex={1}
            >
              <Text isTruncated>
                {fileName != null ? fileName : <Trans>Select CSV file</Trans>}
              </Text>
            </Button>
            {parsedEntries.length > 0 ? (
              <Button
                type="button"
                size="sm"
                colorScheme="brand"
                onClick={handleUpload}
                isLoading={isUploading}
                flexShrink={0}
              >
                <Trans>Import {parsedEntries.length} entries</Trans>
              </Button>
            ) : null}
          </HStack>

          {parseError != null ? (
            <Alert status="error" borderRadius="md" fontSize="xs">
              <AlertIcon />
              <AlertDescription>{parseError}</AlertDescription>
            </Alert>
          ) : null}

          {uploadError != null ? (
            <Alert status="error" borderRadius="md" fontSize="xs">
              <AlertIcon />
              <AlertDescription>{uploadError}</AlertDescription>
            </Alert>
          ) : null}

          {uploadResult != null ? (
            <Alert status="success" borderRadius="md" fontSize="xs">
              <AlertIcon />
              <AlertDescription>{uploadResult}</AlertDescription>
            </Alert>
          ) : null}
        </VStack>
      </CardBody>
    </Card>
  );
}
